import { useEditorSession } from '../../../hooks/useEditorSession';
import { recordKey } from '../../collection/multiCollectionIdentity';
import { DialogContent } from '../../../components/ui/DialogContent';
import { useEffect, useState } from 'react';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import { DialogActions } from '../../../components/ui/DialogActions';
import { DialogHeader } from '../../../components/ui/DialogHeader';
import { ResponsiveDialog } from '../../../components/ui/ResponsiveDialog';
import { Location } from '../../../domain/location';
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard';
import { userProfileResolverService } from '../../../services/userProfileResolverService';
import { formatDateTime } from '../../../utils/dateFormat';

export interface LocationUpdate {
  collectionId?: string;
  id: string;
  name: string;
  description: string;
}

export function LocationEditorDialog({
  scopeKey,
  open,
  location,
  onClose,
  onSave,
  onDelete,
  readOnly = false,
  currentUserId,
  currentUsername,
  collectionId,
  membershipVersion,
  onAddItem,
}: {
  scopeKey?: string;
  open: boolean;
  location: Location | null;
  onClose: () => void;
  onSave: (update: LocationUpdate) => Promise<boolean>;
  onDelete: (location: Location) => Promise<boolean>;
  readOnly?: boolean;
  currentUserId?: string;
  currentUsername?: string;
  collectionId?: string;
  membershipVersion?: number;
  onAddItem?: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<'save' | 'delete' | null>(null);
  const [editorUsername, setEditorUsername] = useState<string | null>(null);

  const baseline = useEditorSession(
    open && location ? recordKey(location) : null,
    location,
  );
  const locationId = baseline ? recordKey(baseline) : undefined;
  const initialName = baseline?.name;
  const initialDescription = baseline?.description;

  useEffect(() => {
    let active = true;
    const editorId = location?.lastEditedBy;
    if (!editorId) {
      setEditorUsername(null);
      return;
    }
    if (currentUserId && editorId === currentUserId && currentUsername) {
      setEditorUsername(currentUsername);
      return;
    }
    if (!collectionId || membershipVersion === undefined) {
      setEditorUsername(null);
      return;
    }
    setEditorUsername(null);
    void userProfileResolverService
      .resolveUsername(collectionId, editorId, membershipVersion)
      .then((resolved) => {
        if (active) setEditorUsername(resolved);
      });
    return () => {
      active = false;
    };
  }, [
    location?.lastEditedBy,
    currentUserId,
    currentUsername,
    collectionId,
    membershipVersion,
  ]);

  useEffect(() => {
    if (!open || !locationId) return;
    setName(initialName || '');
    setDescription(initialDescription || '');
    setError(null);
    setPending(null);
  }, [open, locationId, initialName, initialDescription]);

  const isDirty =
    name !== (initialName || '') || description !== (initialDescription || '');

  const discard = () => {
    setName(initialName || '');
    setDescription(initialDescription || '');
    setError(null);
  };

  const { requestClose, closeAfterSave } = useUnsavedChangesGuard({
    scopeKey,
    open,
    dirty: isDirty,
    pending: Boolean(pending),
    onDiscard: discard,
    onClose,
  });

  const close = () => {
    void requestClose();
  };

  const save = async () => {
    if (!location || pending) return;
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Location name is required.');
      return;
    }

    setError(null);
    setPending('save');
    const succeeded = await onSave({
      ...(location.collectionId ? { collectionId: location.collectionId } : {}),
      id: location.id,
      name: trimmedName,
      description: description.trim(),
    });
    setPending(null);
    if (succeeded) closeAfterSave();
    else setError('Location could not be saved. Please try again.');
  };

  const remove = async () => {
    if (!location || pending) return;
    setError(null);
    setPending('delete');
    const succeeded = await onDelete(location);
    setPending(null);
    if (succeeded) closeAfterSave();
    else setError('Location was not deleted.');
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={close}
      aria-labelledby="location-editor-title"
    >
      <DialogHeader
        id="location-editor-title"
        title={`${readOnly ? 'Location details' : 'Edit location'}${location?.collectionName ? ` · ${location.collectionName}` : ''}`}
        onClose={close}
        closeDisabled={Boolean(pending)}
      />
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {error && (
          <Alert severity="error" role="alert">
            {error}
          </Alert>
        )}
        <TextField
          autoFocus
          required
          label="Location name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          error={Boolean(error && !name.trim())}
          disabled={readOnly || Boolean(pending)}
          fullWidth
        />
        <TextField
          label="Description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          disabled={readOnly || Boolean(pending)}
          multiline
          fullWidth
        />
        {location &&
          (location.lastEditedAt ||
            location.lastEditedBy ||
            location.createdAt) && (
            <Box
              data-testid="location-metadata"
              sx={{
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
                display: 'grid',
                gap: 0.75,
              }}
            >
              {location.createdAt && (
                <Typography variant="caption" color="text.secondary">
                  Created: {formatDateTime(location.createdAt)}
                </Typography>
              )}
              {location.lastEditedAt && (
                <Typography variant="caption" color="text.secondary">
                  Edited at: {formatDateTime(location.lastEditedAt)}
                  {editorUsername ? ` by @${editorUsername}` : ''}
                </Typography>
              )}
              {!location.lastEditedAt && location.lastEditedBy && (
                <Typography variant="caption" color="text.secondary">
                  Edited by:{' '}
                  {editorUsername ? `@${editorUsername}` : 'Unknown user'}
                </Typography>
              )}
            </Box>
          )}
      </DialogContent>
      {!readOnly && onAddItem && (
        <Box sx={{ px: 3, pb: 1 }}>
          <Button onClick={onAddItem} disabled={Boolean(pending)}>
            Add item here
          </Button>
        </Box>
      )}
      <DialogActions sx={{ display: readOnly ? 'none' : undefined }}>
        <Button
          color="error"
          variant="outlined"
          onClick={remove}
          disabled={Boolean(pending)}
        >
          {pending === 'delete' ? 'Deleting…' : 'Delete'}
        </Button>
        <Button onClick={close} disabled={Boolean(pending)}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={Boolean(pending)}>
          {pending === 'save' ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
